import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Table, Customer, BasketItem, BasketItemExtra, NotificationTyp } from '../../services/IInHouseOrderService';
import { InHouseOrderService } from '../../services/inHouseOrder.service';
import { WebShopService } from '../../services/webShop.service';
import { WebShopMode } from '../../shared/contentTypes';
import { MAX_BASKETITEM_COUNT, WebshopSettingsDataModel2 } from '../../shared/models';
import { IGroupBasketItem, IGroupMemberItems, IGroupOverviewService, IMenuCourse, IGroupMember, IGroupInfo, IGroupBasketItemExtra } from './groupOrderOverview.facade';
import Utils from '../../shared/utils';
import { InternationalisationService } from '../../forked/angular-internationalisation';
import { BottomSheetHelperService } from '../../services/bottomSheetHelper.service';
import { Router } from '@angular/router';
import { WarningdialogComponent } from '../../modalDialogs';
import { CircularInjectionHelper } from '../../services/circularInjectionHelper';
import { CoreLogService } from '../../services/log.service';

export class GroupMember implements IGroupMember {

  get userToken(): string {
    return this._customer.user_token;
  }
  get name(): string {
    return this._customer.nickname;
  }

  groupBasket_itemsCount: number;
  groupBasket_itemsAmount: number;
  ordered_itemsCount: number;
  ordered_itemsAmount: number;

  get isOrdering(): boolean {
    return this._customer.is_ordering;
  }

  get hasNotification(): boolean {
    return this._customer.notification_typ !== NotificationTyp.NONE;
  }

  constructor(private _customer: Customer, table: Table) {
    const me = this;
    const userToken = _customer.user_token;

    me.groupBasket_itemsCount = 0;
    me.groupBasket_itemsAmount = 0;
    table.yasTable.group_basket_items.forEach(item => {
      if (item.user_token != userToken)
        return true; // continue;
      me.groupBasket_itemsCount += item.amount;
      me.groupBasket_itemsAmount += item.amount * item.price;
    });
    me.groupBasket_itemsAmount = Utils.centsToCurrency(me.groupBasket_itemsAmount);

    me.ordered_itemsCount = 0;
    me.ordered_itemsAmount = 0;
    table.posTable.items.forEach(item => {
      if (item.userToken != userToken)
        return true; // continue;
      me.ordered_itemsCount += item.amount;
      me.ordered_itemsAmount += item.amount * item.price;
    });
    me.ordered_itemsAmount = Utils.centsToCurrency(me.ordered_itemsAmount);
  }
}

class GroupBasketItemExtra implements IGroupBasketItemExtra {
  get title(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(this._model.name_en) ? this._model.name_de : this._model.name_en;
  }
  get subIndex(): number {
    return 0; //! todo interne verknüpfung muss möglichsein
  }
  get count(): number {
    return this._model.amount;
  }

  constructor(
    private _model: BasketItemExtra
  ) { }
}

class GroupBasketItem implements IGroupBasketItem {

  extraItems: GroupBasketItemExtra[];

  constructor(
    private _service: GroupOrderOverviewService,
    private _model: BasketItem,
    private isOwner: boolean,
    private isCreator: boolean,
    menucourses: IMenuCourse[]
  ) {

    const me = this;
    me.hasComment = me._model.freetext !== "";

    me.extraItems = _model.extras.map((extra) => new GroupBasketItemExtra(extra));

    // prüfe menügang, wenn es keines von den Angebotenen ist, dann entfernen
    let item_menu_course: IMenuCourse = null;
    if (me._model.menu_course > 0) {
      item_menu_course = menucourses.find((menu_course) => menu_course.id == me._model.menu_course);
    }
    if (!item_menu_course)
      me._use_menucourse = false;
  }

  get id(): number {
    return this._model.product_id;
  }
  get label(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(this._model.name_en) ? this._model.name_de : this._model.name_en;
  }
  get valid_date(): number {
    return this._model.validDate_YMD;
  }
  get deposit(): number {
    return 0;
  }
  get variable_price(): number {
    return 0;
  }
  public get extras(): IGroupBasketItemExtra[] {
    return this.extraItems;
  }
  get count(): number {
    return this._model.amount;
  }
  // Einzelpreis + Deposit
  get price(): number {
    return Utils.centsToCurrency(this._model.price * this._model.amount);
  }
  public get comment(): string {
    return this._model.freetext;
  }
  public setcomment(value: string) {
    if (this.canComment) {
      this._model.freetext = value;
      this._service.updateItem(this._model);
    }
  }
  public hasComment: boolean = false;

  public get canMenucourse(): boolean {
    return this.isOwner;
  }

  private _use_menucourse: boolean = true; // initial ist es erlaubt
  public get use_menucourse(): boolean {
    return this._use_menucourse;
  }

  public get menucourse(): number {
    return this._model.menu_course;
  }

  public set menucourse(value: number) {
    if (this.canMenucourse) {
      this._model.menu_course = value;
      this._service.updateItem(this._model);
    }
  }
  //! Currently, only changing the count is allowed.
  //! A comment can be added in the dialog before the item is added to the basket (e.g., Order directly).
  public get canComment(): boolean {
    return (this.isOwner || this.isCreator) && this._service.canComment;
  }

  get canIncrement(): boolean {
    return (this.isOwner || this.isCreator) && (this._model.amount < MAX_BASKETITEM_COUNT);
  }
  get canDecrement(): boolean {
    return this.isOwner || this.isCreator; // bei 0 wird artikel entfernt
  }

  increment() {
    if (this.canIncrement) {
      this._service.addCountItem(this._model);
    }
  }

  decrement() {
    if (this.canDecrement) {
      this._service.removeCountItem(this._model);
    }
  }
}

class GroupMemberItems implements IGroupMemberItems {

  get userToken(): string {
    return this._customer.user_token;
  }
  get name(): string {
    return this._customer.nickname;
  }

  items: IGroupBasketItem[];

  constructor(
    private _customer: Customer,
    service: GroupOrderOverviewService,
    table: Table,
    ownerUserToken: string,
    ownerName: string,
    menucourses: IMenuCourse[]) {
    const me = this;
    const cus_userToken = _customer.user_token;
    const isOwner = cus_userToken == ownerUserToken;
    const isCreator = service.GroupInfo.groupCreator === ownerName;
    const user_group_basket_items = table.yasTable.group_basket_items
      .filter((item) => item.user_token == cus_userToken)
      .sort((a, b) => a.position - b.position);
    me.items = user_group_basket_items.map((item) => new GroupBasketItem(service, item, isOwner, isCreator, menucourses));
  }
}

@Injectable()
export class GroupOrderOverviewService implements IGroupOverviewService, OnDestroy {
  private _webshopSettingsChangedSub: Subscription;
  private _inHouseTableChangedSub: Subscription;
  private _inHouseTableClosedSub: Subscription;

  constructor(private _webShopService: WebShopService
    , private _inHouseOrderService: InHouseOrderService
    , private _bottomSheetHelperService: BottomSheetHelperService
    , private _router: Router
    , public _objObsInjectHelper: CircularInjectionHelper
    , private _coreLogService: CoreLogService

  ) {
    const me = this;

    me.GroupInfo = {
      groupName: me._inHouseOrderService.GroupName,
      groupCreator: me._inHouseOrderService.GroupCreator
    }

    me.initial().catch((error) => {
      me._coreLogService.error("GroupOrderOverviewService.constructor", "initial failed", error);
    });

    me._webshopSettingsChangedSub = me._webShopService.webshopSettingsDataModelObserveChanges.subscribe(async (_) => {
      const webshopSetting = await me._webShopService.getWebshopSettingsDataModel();
      me.rebindWebshopSetting(webshopSetting);
    })

    me._inHouseTableChangedSub = me._inHouseOrderService.ObserveOnTableChanged.subscribe(async (_) => {
      const table = await me._inHouseOrderService.GetTable(null); // null damit kein unnötiger Abruf stattfindet
      me.rebindInHouseTable(table);
    })

    me._inHouseTableClosedSub = me._inHouseOrderService.ObserveOnTableClosedChanged.subscribe((_) => {
      me.OpenWarningLinkExpired();
    })
  }

  ngOnDestroy(): void {
    // Führt Cleanup durch
    const me = this;

    if (me._webshopSettingsChangedSub)
      me._webshopSettingsChangedSub.unsubscribe();
    if (me._inHouseTableChangedSub)
      me._inHouseTableChangedSub.unsubscribe();
    if (me._inHouseTableClosedSub)
      me._inHouseTableClosedSub.unsubscribe();
  }

  private async initial(): Promise<void> {
    const me = this;

    try {
      const webshopSetting = await me._webShopService.getWebshopSettingsDataModel();
      me.rebindWebshopSetting(webshopSetting);

      const table = await me._inHouseOrderService.GetTable(null); // null damit kein unnötiger Abruf stattfindet
      me.rebindInHouseTable(table);
    } catch (error) {
      // Wenn GetTable fehlschlägt (z.B. "not connected"), warten wir auf OnTableChanged Event
      me._coreLogService.error("GroupOrderOverviewService.initial", "GetTable failed, waiting for OnTableChanged", error);
    }
  }

  /** anzahl der teilnehmer an einer Gruppe */
  private participants_per_group: number = 0;

  private rebindWebshopSetting(webshopSetting: WebshopSettingsDataModel2) {
    const me = this;

    // todo ermitteln ob  _canComment und _menucourses sich geändert haben, den nur dann ist ein rebind sinnvoll

    me.canComment = webshopSetting.use_item_annotation;
    if (webshopSetting.in_house_order && webshopSetting.in_house_order.grouporder)
      me.participants_per_group = webshopSetting.in_house_order.grouporder.participants_per_group | 0;

    // momentan werden Menügänge nur beim InHouse angeboten
    if (webshopSetting.use_menu_course && me._webShopService.getServiceMode() === WebShopMode.OrderToTable) {
      let menu_courses = webshopSetting.menu_courses.sort((l, r) => l.sortnumber - r.sortnumber);
      // nur menügänge mit sortnumber 1-3
      menu_courses = menu_courses.filter((course) => course.sortnumber > 0 && course.sortnumber < 4);
      // nur die ersten 3 menügänge
      menu_courses = menu_courses.slice(0, 3);
      me._menucourses = menu_courses;
      me._menucoursesSubject.next(me._menucourses);
    } // else keine Menügänge, ui wird sie dann nicht darstellen

    // todo rebind items wegen _canComment und den _menucourses
  }

  private async rebindInHouseTable(table: Table): Promise<void> {
    const me = this;

    const currentUserToken = me._inHouseOrderService.UserToken;
    const currentUserName = me._inHouseOrderService.UserNickname;
    me._groupMember = table.yasTable.customers.map((customer) => new GroupMember(customer, table));
    // put self to first place
    const currentMemberIndex = me._groupMember.findIndex((member) => member.userToken == currentUserToken);
    if (currentMemberIndex > 0) { // ">0" weil 0 bereits erster ist
      me._groupMember.unshift(me._groupMember.splice(currentMemberIndex, 1)[0]);
    }
    me._groupMemberSubject.next(me._groupMember);

    const userToken_with_group_basket_items = Array.from(new Set(table.yasTable.group_basket_items.map((item) => item.user_token)));
    const customer_with_group_basket_items = table.yasTable.customers.filter((cus) => userToken_with_group_basket_items.includes(cus.user_token));
    me._groupMemberItemsGroups = customer_with_group_basket_items.map((customer) => new GroupMemberItems(customer, me, table, currentUserToken, currentUserName, me._menucourses));
    me._groupMemberItemsGroupsSubject.next(me._groupMemberItemsGroups);

    let groupBasket_itemsCount = 0;
    let groupBasket_itemsAmount = 0;
    table.yasTable.group_basket_items.forEach(item => {
      groupBasket_itemsCount += item.amount;
      groupBasket_itemsAmount += item.amount * item.price;
    });
    me._itemCountSubject.next(groupBasket_itemsCount);
    me._openPriceSubject.next(Utils.centsToCurrency(groupBasket_itemsAmount));

    me._readyforpickupCountSubject.next(me._inHouseOrderService.GetReadyForPickUpCount(table));
    me._groupFullSubject.next(me.participants_per_group > table.yasTable.customers.length);
  }

  public GroupInfo: IGroupInfo;

  public canComment: boolean = false;

  private _menucourses: IMenuCourse[] = [];
  private _menucoursesSubject: BehaviorSubject<IMenuCourse[]> = new BehaviorSubject<IMenuCourse[]>([]);
  get menucourses$(): Observable<IMenuCourse[]> {
    return this._menucoursesSubject.asObservable();
  }

  private _groupMember: GroupMember[] = [];
  private _groupMemberSubject: BehaviorSubject<IGroupMember[]> = new BehaviorSubject<IGroupMember[]>([]);
  get groupMember$(): Observable<IGroupMember[]> {
    return this._groupMemberSubject.asObservable();
  }

  private _groupMemberItemsGroups: GroupMemberItems[] = [];
  private _groupMemberItemsGroupsSubject: BehaviorSubject<IGroupMemberItems[]> = new BehaviorSubject<IGroupMemberItems[]>([]);
  public get groupMemberItemsGroups$(): Observable<IGroupMemberItems[]> {
    return this._groupMemberItemsGroupsSubject.asObservable();
  }

  private _openPriceSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get open_price$(): Observable<number> {
    return this._openPriceSubject.asObservable();
  }

  private _itemCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get item_count$(): Observable<number> {
    return this._itemCountSubject.asObservable();
  }

  private _readyforpickupCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  get readyforpickupCount$(): Observable<number> {
    return this._readyforpickupCountSubject.asObservable();
  }

  private _groupFullSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public get groupFull$(): Observable<boolean> {
    return this._groupFullSubject.asObservable();
  }

  /**
   * Updates a single item in the group basket
   * @returns update state
   *
   * Note: This method uses Full sync mode to preserve item positions in the basket.
   * The item is identified by the combination of user_token, position, and product_id.
   */
  public async updateItem(item: BasketItem): Promise<boolean> {
    const me = this;
    return await me._inHouseOrderService.GroupBasket_UpdateItem(item);
  }


  /**
   * Increases item amount by 1 in the group basket
   * Item is identified by user_token + position from the BasketItem
   * Only allowed for group creator and owner of the item
   */
  public async addCountItem(item: BasketItem): Promise<void> {
    const me = this;
    await me._inHouseOrderService.GroupBasket_AddCount(item);
  }


  /**
   * Decreases item amount by 1 in the group basket
   * If amount reaches 0, item will be removed from the basket
   * Item is identified by user_token + position from the BasketItem
   * Only allowed for group creator and owner of the item
   */
  public async removeCountItem(item: BasketItem): Promise<void> {
    const me = this;
    await me._inHouseOrderService.GroupBasket_RemoveCount(item);
  }

  /** Warning dialog: table / group closed */
  private OpenWarningLinkExpired() {
    const me = this;
    const warningButtomSheet = me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: "phGroupOrder_LinkExpired_Title"
        , content: "phGroupOrder_LinkExpired_SubText"
        , btnLabel: "phGroupOrder_LinkExpired_Btn"
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });
    warningButtomSheet.afterDismissed().subscribe((result) => {
      if (result) {
        me._router.navigate(["/menuservice"]);
      }
    });
  }
}
